/**
 * Notification center Zustand store (B15, CF-2400).
 *
 * Two notions of "read":
 *  - `read_at` (server, permanent) — set on click or "mark all read".
 *  - "seen this session" (ephemeral, in-memory) — a notification visible ≥2s
 *    in the dropdown stops counting toward the number badge for THIS session,
 *    but stays unread in the DB. Reset on tab refresh (store re-created).
 *
 * Badge:
 *  - number  = unread (read_at null) AND NOT seen-this-session.
 *  - red dot = number === 0 AND ≥1 sticky-important unresolved notification.
 */

import { create } from 'zustand';
import type { NotificationDto } from '@shared/notification.js';
import {
  listNotifications,
  getUnreadCount,
  markNotificationRead,
  markAllNotificationsRead,
} from '../api/notificationsApi';

interface NotificationState {
  notifications: NotificationDto[];
  unreadCount: number;          // server-authoritative total unread
  seenThisSession: Set<number>; // ephemeral
  hydrated: boolean;
  loading: boolean;
  hydrate: () => Promise<void>;
  addRealtime: (dto: NotificationDto) => void;
  markSeen: (id: number) => void;
  markRead: (id: number) => Promise<void>;
  markAllRead: () => Promise<void>;
}

export const useNotificationStore = create<NotificationState>((set, get) => ({
  notifications: [],
  unreadCount: 0,
  seenThisSession: new Set<number>(),
  hydrated: false,
  loading: false,

  hydrate: async () => {
    if (get().loading) return;
    set({ loading: true });
    try {
      const [notifications, unreadCount] = await Promise.all([listNotifications(), getUnreadCount()]);
      set({ notifications, unreadCount, hydrated: true, loading: false });
    } catch {
      set({ loading: false });
    }
  },

  addRealtime: (dto) => {
    set((state) => {
      if (state.notifications.some((n) => n.id === dto.id)) return state;
      return {
        notifications: [dto, ...state.notifications].slice(0, 50),
        unreadCount: dto.read_at === null ? state.unreadCount + 1 : state.unreadCount,
      };
    });
  },

  markSeen: (id) => {
    set((state) => {
      if (state.seenThisSession.has(id)) return state;
      const next = new Set(state.seenThisSession);
      next.add(id);
      return { seenThisSession: next };
    });
  },

  markRead: async (id) => {
    const target = get().notifications.find((n) => n.id === id);
    if (target === undefined || target.read_at !== null) return;
    const nowIso = new Date().toISOString();
    set((state) => ({
      notifications: state.notifications.map((n) => (n.id === id ? { ...n, read_at: nowIso } : n)),
      unreadCount: Math.max(0, state.unreadCount - 1),
    }));
    try {
      await markNotificationRead(id);
    } catch {
      // best-effort; next hydrate reconciles
    }
  },

  markAllRead: async () => {
    const nowIso = new Date().toISOString();
    set((state) => ({
      notifications: state.notifications.map((n) => (n.read_at === null ? { ...n, read_at: nowIso } : n)),
      unreadCount: 0,
      seenThisSession: new Set<number>(),
    }));
    try {
      await markAllNotificationsRead();
    } catch {
      // best-effort
    }
  },
}));

/** Number badge count: unread minus seen-this-session (among still-unread). */
export function selectBadgeCount(state: NotificationState): number {
  let seenUnread = 0;
  for (const n of state.notifications) {
    if (n.read_at === null && state.seenThisSession.has(n.id)) {
      seenUnread += 1;
    }
  }
  return Math.max(0, state.unreadCount - seenUnread);
}

/** Red dot shows when number badge is 0 but a sticky-important is unresolved. */
export function selectHasStickyDot(state: NotificationState): boolean {
  if (selectBadgeCount(state) > 0) return false;
  return state.notifications.some((n) => n.is_sticky_important && n.resolved_at === null);
}
